/**
 * The app-wide DndContext for tree lists (Board cards, item page children) plus the
 * shell's drop zones (top tabs, Pages sidebar). Long-press 250 ms / 5 px on touch, 4 px
 * on mouse (SPEC §8.1). Rows can be reordered, re-nested (horizontal offset → depth),
 * moved into other cards or promoted to a card; rows, cards and pages can be dropped on
 * the tab bar (pin as a tab) or the Pages tree (become a page / subpage).
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import {
  closestCenter,
  DndContext,
  DragOverlay,
  MeasuringStrategy,
  MouseSensor,
  pointerWithin,
  TouchSensor,
  useDndContext,
  useSensor,
  useSensors,
  type CollisionDetection,
  type DragEndEvent,
  type DragMoveEvent,
  type DragOverEvent,
  type DragStartEvent,
} from '@dnd-kit/core'
import { arrayMove, verticalListSortingStrategy, type SortingStrategy } from '@dnd-kit/sortable'
import { dropPosition, projectDrop, withoutDescendants, type FlatRow, type Projection } from './projection'
import { actions } from '../store/actions'
import { store } from '../store/store'
import { displayTitle } from '@terrazzo/core'
import { gestureState, stopEditing, editStore, holdEditForDrag, releaseEditAfterDrag } from '../store/ui'
import { haptic } from '../lib/haptics'
import { zoomBackIn, zoomOutAround } from '../lib/zoom'
import { uiScale } from '../lib/scale'
import { viewItemTitle } from '../lib/viewTitle'
import { homeOf } from '../lib/charts'
import { Row, markDragEnd, type RowNav } from '../components/Row'
import { tintClass } from '../lib/tint'

export const INDENT = 22

export interface ListReg {
  rootId: string
  maxDepth: number
  getRows: () => FlatRow[]
}

/**
 * Where a drop on a page in the sidebar goes, from the pointer's height on it; for a tab
 * being reordered, which side of the tab it's over (before / after).
 */
export type PageDrop = 'before' | 'inside' | 'after'

/** How long a card is held over another before it'll go inside it (instead of above/below). */
const CARD_DWELL_MS = 600

/**
 * Cancel the drag in progress, if any (the pointer sensors cancel on Escape). A tab held
 * still is lifted for a drag on phones before its hold opens the editor.
 */
export function cancelActiveDrag() {
  if (gestureState.get() === 'drag') document.dispatchEvent(new KeyboardEvent('keydown', { code: 'Escape', bubbles: true }))
}

export interface DragState {
  activeId: string | null
  /** What's being dragged: a checklist row, a whole card, a page in the sidebar, a tab, or a chart (by its name). */
  kind: 'row' | 'card' | 'page' | 'tab' | 'chart'
  /** A card's key in the tab it's on (item id, or a group like a tag) and its name, for rearranging. */
  cardKey: string | null
  label: string | null
  pageDrop: PageDrop | null
  fromRoot: string | null
  overRoot: string | null
  overId: string | null
  /** Insert position when the active row came from another list. */
  insertAt: number
  offsetX: number
  projection: Projection | null
  /** A row over a board's open space (a gap, beside or below the cards): it'll be a card of its own, by this one. */
  newCard: { target: string; side: 'before' | 'after' } | null
}

const EMPTY: DragState = {
  activeId: null,
  kind: 'row',
  cardKey: null,
  label: null,
  pageDrop: null,
  fromRoot: null,
  overRoot: null,
  overId: null,
  insertAt: -1,
  offsetX: 0,
  projection: null,
  newCard: null,
}

interface Ctx {
  state: DragState
  register: (reg: ListReg) => () => void
}

const TreeDndCtx = createContext<Ctx | null>(null)

export function useTreeDnd(): Ctx | null {
  return useContext(TreeDndCtx)
}

const NOOP_NAV: RowNav = {
  prev: () => null,
  next: () => null,
  prevSibling: () => null,
  canOutdent: () => false,
  hasOpenChildren: () => false,
  addAfter: () => null,
  addManyAfter: () => [],
}

/**
 * Sorting strategy for a list: the normal vertical shuffle for reorders inside it, none
 * when it's receiving a row from another list (displayRows already placed the row).
 */
export const noSorting: SortingStrategy = () => null
export function listStrategy(rootId: string, st: DragState | undefined): SortingStrategy {
  return st?.activeId && st.overRoot === rootId && st.fromRoot !== rootId ? noSorting : verticalListSortingStrategy
}

/** Rows a list should display right now, given the drag state. */
export function displayRows(base: FlatRow[], rootId: string, st: DragState): FlatRow[] {
  if (!st.activeId || st.kind !== 'row') return base
  let rows = base
  if (st.fromRoot === rootId) {
    rows = withoutDescendants(rows, st.activeId)
    // Gone to another list (or a zone), it leaves this one; over the board's open space it keeps
    // its place, so the card doesn't shrink under the pointer (and pull the gap away from it).
    if (st.overRoot && st.overRoot !== rootId && st.overRoot !== 'cards:area') rows = rows.filter((r) => r.id !== st.activeId)
  }
  if (st.overRoot === rootId && st.fromRoot !== rootId) {
    const depth = st.projection?.depth ?? 0
    const parentId = st.projection?.parentId ?? rootId
    const at = st.insertAt < 0 ? rows.length : Math.min(st.insertAt, rows.length)
    rows = [...rows.slice(0, at), { id: st.activeId, depth, parentId }, ...rows.slice(at)]
  }
  return rows
}

/** Nested providers (Board, item page) reuse the app-wide one from the shell. */
export function TreeDndProvider({ children }: { children: ReactNode }) {
  const parent = useContext(TreeDndCtx)
  return parent ? <>{children}</> : <TreeDndRoot>{children}</TreeDndRoot>
}

function TreeDndRoot({ children }: { children: ReactNode }) {
  const lists = useRef(new Map<string, ListReg>())
  const [state, setState] = useState<DragState>(EMPTY)
  const stateRef = useRef(state)
  stateRef.current = state

  // Holding a card still over another card for a moment: it goes inside that card, not beside it.
  const dwell = useRef<{ target: string | null; ready: string | null; timer: ReturnType<typeof setTimeout> | null }>({ target: null, ready: null, timer: null })
  const resetDwell = () => {
    if (dwell.current.timer) clearTimeout(dwell.current.timer)
    dwell.current = { target: null, ready: null, timer: null }
  }
  // Moving a card zooms the page's cards out around the finger, and back in around where it
  // landed (lib/zoom). Where everything is gets measured again once it's still.
  const measure = useRef<(() => void) | null>(null)
  const cardSel = (dragId: string) => `[data-drag="${CSS.escape(dragId)}"]`
  const zoomInAround = (dragId: string) =>
    // After the move has rendered: the card is where it landed.
    requestAnimationFrame(() => requestAnimationFrame(() => zoomBackIn(document.querySelector(cardSel(dragId)), () => measure.current?.())))
  // Where the pointer is (as collision detection sees it): above or below a card goes by it.
  const pointer = useRef<{ x: number; y: number } | null>(null)

  const trackDwell = (st: DragState, over: string | null) => {
    if (st.kind !== 'card' || over === dwell.current.target) return
    resetDwell()
    if (!over?.startsWith('zone:card:') || over === `zone:card:${st.activeId}`) return
    dwell.current.target = over
    dwell.current.timer = setTimeout(() => {
      const cur = stateRef.current
      if (cur.overRoot !== over) return
      dwell.current.ready = over
      haptic(12)
      setState({ ...cur, pageDrop: 'inside' })
    }, CARD_DWELL_MS)
  }

  const register = useCallback((reg: ListReg) => {
    lists.current.set(reg.rootId, reg)
    return () => {
      if (lists.current.get(reg.rootId) === reg) lists.current.delete(reg.rootId)
    }
  }, [])

  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 4 } }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 250, tolerance: 5 },
    }),
  )

  // After a drag activates, stop the page from scrolling under the finger (SPEC §8.2).
  useEffect(() => {
    const block = (e: TouchEvent) => {
      if (gestureState.get() === 'drag') e.preventDefault()
    }
    document.addEventListener('touchmove', block, { passive: false })
    return () => {
      document.removeEventListener('touchmove', block)
      // Navigating away mid-drag: don't leave taps/scrolling blocked app-wide.
      if (gestureState.get() === 'drag') gestureState.set('none')
    }
  }, [])

  const collision: CollisionDetection = useCallback((args) => {
    pointer.current = args.pointerCoordinates
    const within = pointerWithin(args)
    // A tab being reordered lands beside another tab, and only while over the tab bar.
    if ((args.active.data.current as { type?: string } | undefined)?.type === 'tab') {
      if (!within.some((c) => c.id === 'zone:tabs')) return []
      return closestCenter({ ...args, droppableContainers: args.droppableContainers.filter((c) => String(c.id).startsWith('zone:tab:')) })
    }
    // A chart only goes onto another tab (it moves there).
    if ((args.active.data.current as { type?: string } | undefined)?.type === 'chart') {
      const tab = within.find((c) => String(c.id).startsWith('zone:tab:'))
      return tab ? [tab] : []
    }
    // A specific tab (add to it) wins over the tab bar around it (new tab).
    const zone = within.find((c) => String(c.id).startsWith('zone:tab:')) ?? within.find((c) => String(c.id).startsWith('zone:'))
    if (zone) return [zone]
    // A card being rearranged, in a gap or below a column: next to the nearest card.
    if ((args.active.data.current as { type?: string } | undefined)?.type === 'card' && within.some((c) => c.id === 'cards:area')) {
      return closestCenter({ ...args, droppableContainers: args.droppableContainers.filter((c) => /^zone:v?card:/.test(String(c.id))) })
    }
    // Whole cards and pages only drop on zones (tabs, Pages), never into checklists.
    if ((args.active.data.current as { type?: string } | undefined)?.type !== 'row') return []
    const list = within.find((c) => String(c.id).startsWith('list:'))
    const root = list ? (list.data?.droppableContainer?.data.current as { root?: string } | undefined)?.root : undefined
    if (root) {
      const rows = args.droppableContainers.filter((c) => (c.data.current as { root?: string; type?: string } | undefined)?.root === root && c.data.current?.type === 'row')
      if (rows.length) {
        const hit = closestCenter({ ...args, droppableContainers: rows })
        if (hit.length) return hit
      }
      return [list!]
    }
    // In a board's open space (not over any card): it'll be a card of its own there.
    const area = within.find((c) => c.id === 'cards:area')
    if (area && args.pointerCoordinates && !overACard(args.pointerCoordinates)) return [area]
    return closestCenter({
      ...args,
      droppableContainers: args.droppableContainers.filter((c) => c.data.current?.type === 'row'),
    })
  }, [])

  const compute = (st: DragState): DragState => {
    if (!st.activeId || !st.overRoot) return { ...st, projection: null }
    const reg = lists.current.get(st.overRoot)
    if (!reg) return { ...st, projection: null }
    let items: FlatRow[]
    let overId = st.overId
    if (st.fromRoot === st.overRoot) {
      items = withoutDescendants(reg.getRows(), st.activeId)
      if (!overId || !items.some((r) => r.id === overId)) overId = items[items.length - 1]?.id ?? null
      if (!overId) return { ...st, projection: null }
    } else {
      const base = reg.getRows().filter((r) => r.id !== st.activeId)
      const at = st.insertAt < 0 ? base.length : Math.min(st.insertAt, base.length)
      const activeDepth = 0
      items = [...base.slice(0, at), { id: st.activeId, depth: activeDepth, parentId: st.overRoot }, ...base.slice(at)]
      overId = st.activeId
    }
    const p = projectDrop(items, st.overRoot, st.activeId, overId, st.offsetX, INDENT * uiScale(), reg.maxDepth)
    return { ...st, projection: p }
  }

  const onDragStart = (e: DragStartEvent) => {
    const data = e.active.data.current as { root?: string; type?: string; id?: string; key?: string; label?: string } | undefined
    const kind = data?.type === 'card' || data?.type === 'page' || data?.type === 'tab' || data?.type === 'chart' ? data.type : 'row'
    // A group card in a filtered tab (e.g. "#kitchen") has no item, only its key.
    const id = kind === 'row' ? String(e.active.id) : String(data?.id ?? `key:${data?.key}`)
    const root = kind === 'row' ? (data?.root ?? null) : null
    if (kind === 'card') {
      const area = (e.activatorEvent.target as Element | null)?.closest?.('[data-cards-area]') as HTMLElement | null
      const at = pointOf(e.activatorEvent)
      if (area && at) zoomOutAround(area, at.x, at.y, () => measure.current?.())
    }
    gestureState.set('drag')
    // Dragging the row you're typing in keeps editing (and the keyboard); any other drag
    // ends editing first.
    if (!(kind === 'row' && holdEditForDrag(id))) {
      if (editStore.get().id) stopEditing()
      ;(document.activeElement as HTMLElement | null)?.blur?.()
    }
    haptic(10)
    setState(
      compute({
        ...EMPTY,
        activeId: id,
        kind,
        cardKey: kind === 'card' ? (data?.key ?? id) : null,
        label: data?.label ?? null,
        fromRoot: root,
        overRoot: root,
        overId: id,
      }),
    )
  }

  const overInfo = (e: DragOverEvent | DragMoveEvent) => {
    const over = e.over
    if (!over)
      return {
        overRoot: null as string | null,
        overId: null as string | null,
        insertAt: -1,
        crossX: 0,
      }
    const data = over.data.current as { type?: string; root?: string } | undefined
    const overId = String(over.id)
    if (overId === 'cards:area' && (e.active.data.current as { type?: string } | undefined)?.type === 'row') {
      const p = pointer.current
      return { overRoot: overId, overId: null, insertAt: -1, crossX: 0, newCard: p ? newCardSpot(p) : null }
    }
    if (overId.startsWith('zone:')) {
      // On a page in the sidebar: top quarter = before it, bottom quarter = after, else inside.
      let pageDrop: PageDrop | null = null
      if (overId.startsWith('zone:page:')) {
        const a = e.active.rect.current.translated
        const y = a ? a.top + a.height / 2 : 0
        const r = over.rect
        pageDrop = y < r.top + r.height * 0.25 ? 'before' : y > r.bottom - r.height * 0.25 ? 'after' : 'inside'
      } else if (overId.startsWith('zone:card:') || overId.startsWith('zone:vcard:')) {
        // Rearranging cards: above or below the one it's over (by the pointer: the card being
        // dragged sits wherever the zoom left it) — or, after holding still over it, inside.
        const a = e.active.rect.current.translated
        const y = pointer.current?.y ?? (a ? a.top + a.height / 2 : 0)
        pageDrop = dwell.current.ready === overId ? 'inside' : y < over.rect.top + over.rect.height / 2 ? 'before' : 'after'
      } else if (overId.startsWith('zone:tab:') && (e.active.data.current as { type?: string } | undefined)?.type === 'tab') {
        const a = e.active.rect.current.translated
        const x = a ? a.left + a.width / 2 : 0
        pageDrop = x < over.rect.left + over.rect.width / 2 ? 'before' : 'after'
      }
      return { overRoot: overId, overId: null, insertAt: -1, crossX: 0, pageDrop }
    }
    const root = data?.root ?? null
    if (!root) return { overRoot: null, overId: null, insertAt: -1, crossX: 0 }
    const activeRect = e.active.rect.current.translated
    // Across lists, depth comes from where the row sits relative to the target list's edge.
    const crossX = activeRect && over.rect ? activeRect.left - over.rect.left : 0
    if (data?.type === 'list') return { overRoot: root, overId: null, insertAt: -1, crossX }
    const st = stateRef.current
    // The placeholder we inserted into another list is itself a droppable; hovering it
    // means "stay put", not "append".
    if (overId === st.activeId && root === st.overRoot && root !== st.fromRoot) return { overRoot: root, overId, insertAt: st.insertAt, crossX }
    let insertAt = -1
    if (root !== st.fromRoot) {
      const rows = (lists.current.get(root)?.getRows() ?? []).filter((r) => r.id !== st.activeId)
      const i = rows.findIndex((r) => r.id === overId)
      const below = activeRect && over.rect ? activeRect.top + activeRect.height / 2 > over.rect.top + over.rect.height / 2 : false
      insertAt = i < 0 ? rows.length : below ? i + 1 : i
    }
    return { overRoot: root, overId, insertAt, crossX }
  }

  const onDragOver = (e: DragOverEvent) => {
    const st = stateRef.current
    if (!st.activeId) return
    const { crossX, ...info } = overInfo(e)
    const next = compute({
      ...st,
      pageDrop: null,
      newCard: null,
      ...info,
      offsetX: info.overRoot !== st.fromRoot ? crossX : e.delta.x,
    })
    // A small tick when the item reaches a drop zone ("it's got it").
    if (next.overRoot !== st.overRoot && next.overRoot?.startsWith('zone:')) haptic(8)
    trackDwell(st, next.overRoot)
    if (
      next.pageDrop !== st.pageDrop ||
      !sameSpot(next.newCard, st.newCard) ||
      next.overRoot !== st.overRoot ||
      next.overId !== st.overId ||
      next.insertAt !== st.insertAt ||
      next.projection?.depth !== st.projection?.depth ||
      next.projection?.parentId !== st.projection?.parentId
    ) {
      setState(next)
    }
  }

  const onDragMove = (e: DragMoveEvent) => {
    const st = stateRef.current
    if (!st.activeId) return
    const { crossX, ...info } = overInfo(e)
    const next = compute({
      ...st,
      pageDrop: null,
      newCard: null,
      ...info,
      offsetX: info.overRoot !== st.fromRoot ? crossX : e.delta.x,
    })
    // A small tick each time the landing indent changes while dragging.
    if (st.projection && next.projection && next.projection.depth !== st.projection.depth) haptic(4)
    trackDwell(st, next.overRoot)
    if (
      next.pageDrop !== st.pageDrop ||
      !sameSpot(next.newCard, st.newCard) ||
      next.projection?.depth !== st.projection?.depth ||
      next.projection?.parentId !== st.projection?.parentId ||
      next.insertAt !== st.insertAt ||
      next.overRoot !== st.overRoot
    ) {
      setState(next)
    }
  }

  const finish = () => {
    resetDwell()
    gestureState.set('none')
    markDragEnd()
    setState(EMPTY)
    // After the move has been applied (startEditing flushes the re-render first).
    releaseEditAfterDrag()
  }

  const onDragEnd = (e: DragEndEvent) => {
    const st = stateRef.current
    try {
      if (!st.activeId) return
      const overZone = e.over && String(e.over.id).startsWith('zone:') ? String(e.over.id) : null
      if (st.kind === 'card') zoomInAround(String(e.active.id))
      // A card in a filtered tab dropped on another: that tab's own order (the card knows how).
      if (overZone?.startsWith('zone:vcard:')) {
        const place = (e.over?.data.current as { place?: (key: string, side: 'before' | 'after') => void } | undefined)?.place
        if (st.kind === 'card' && st.cardKey) place?.(st.cardKey, st.pageDrop === 'after' ? 'after' : 'before')
        return
      }
      if (overZone) {
        dropOnZone(overZone, st.activeId, st.pageDrop, st.kind)
        return
      }
      if (st.kind === 'row' && st.overRoot === 'cards:area') {
        if (st.newCard) actions.placeCard(st.activeId, st.newCard.target, st.newCard.side, { asNewCard: true })
        return
      }
      if (st.kind !== 'row' || !st.overRoot || !st.projection) return
      const reg = lists.current.get(st.overRoot)
      if (!reg) return
      let moved: FlatRow[]
      let index: number
      if (st.fromRoot === st.overRoot) {
        const items = withoutDescendants(reg.getRows(), st.activeId)
        const from = items.findIndex((r) => r.id === st.activeId)
        const to = st.overId ? items.findIndex((r) => r.id === st.overId) : items.length - 1
        if (from < 0 || to < 0) return
        moved = arrayMove(items, from, to)
        index = to
      } else {
        const base = reg.getRows().filter((r) => r.id !== st.activeId)
        const at = st.insertAt < 0 ? base.length : Math.min(st.insertAt, base.length)
        moved = [
          ...base.slice(0, at),
          {
            id: st.activeId,
            depth: st.projection.depth,
            parentId: st.projection.parentId,
          },
          ...base.slice(at),
        ]
        index = at
      }
      const { depth, parentId } = st.projection
      moved = moved.map((r, i) => (i === index ? { ...r, depth, parentId } : r))
      const pos = dropPosition(moved, index, depth, parentId)
      const cur = store.get(st.activeId)
      const sameParent = (cur?.parentId ?? null) === parentId
      if (sameParent)
        actions.reorder(st.activeId, {
          parentId,
          ...pos,
          ...(pos.afterId || pos.beforeId ? {} : { at: 'start' }),
        })
      else
        actions.move(st.activeId, {
          parentId,
          ...pos,
          ...(pos.afterId || pos.beforeId ? {} : { at: 'start' }),
        })
    } finally {
      finish()
    }
  }

  const ctx = useMemo<Ctx>(() => ({ state, register }), [state, register])
  // Over a drop zone the dragged item shrinks and fades, as if the zone has grabbed it,
  // so the zone's own highlight isn't hidden under it (or under a finger).
  // (Not when rearranging cards or tabs: those zones are where it lands, not what takes it.)
  // A row where it'll become a card (the open, + New card, the strip at the top): it shows as one.
  const asCard = state.kind === 'row' && (!!state.newCard || /^zone:(newcard|top|root:)/.test(state.overRoot ?? ''))
  const overZone =
    !asCard && !!state.overRoot?.startsWith('zone:') && !/^zone:v?card:/.test(state.overRoot) && state.kind !== 'tab' && !(state.kind === 'page' && state.overRoot.startsWith('zone:page:'))

  return (
    <TreeDndCtx.Provider value={ctx}>
      <DndContext
        sensors={sensors}
        collisionDetection={collision}
        measuring={{ droppable: { strategy: MeasuringStrategy.WhileDragging } }}
        onDragStart={onDragStart}
        onDragOver={onDragOver}
        onDragMove={onDragMove}
        onDragEnd={onDragEnd}
        onDragCancel={(e) => {
          if (stateRef.current.kind === 'card') zoomInAround(String(e.active.id))
          finish()
        }}
        // Hovering a drop zone (tab bar, Pages, the phone's bottom bar) shouldn't scroll the page away.
        // A tab being reordered scrolls the tab strip sideways near its ends.
        // A card scrolls the page sooner and faster (a fifth of the screen from either edge).
        autoScroll={
          state.kind === 'tab'
            ? { threshold: { x: 0.12, y: 0 }, acceleration: 10 }
            : state.kind === 'card'
              ? { threshold: { x: 0, y: 0.2 }, acceleration: 20 }
              : { enabled: !overZone, threshold: { x: 0, y: 0.12 }, acceleration: 12 }
        }
      >
        {children}
        <MeasureBridge into={measure} />
        <DragOverlay
          dropAnimation={{
            duration: 160,
            easing: 'cubic-bezier(0.2, 0.8, 0.2, 1)',
          }}
        >
          {state.activeId && state.kind === 'tab' ? (
            // A tab hangs just below the strip, so the gap it would drop into stays in view: its name, underlined.
            <div className="drag-overlay inline-flex h-8 max-w-44 translate-y-8 items-center bg-bg px-2 text-sm font-medium shadow-lg">
              <span className="truncate border-b-[1.5px] border-fg pb-0.5">{viewItemTitle(state.activeId)}</span>
            </div>
          ) : state.activeId && (state.kind !== 'row' || asCard) ? (
            // A card (or a page, a note) being dragged: its bar, lifted and tilted a little (SPEC §7.10).
            <div
              className={`drag-overlay tz-card preview w-64 max-w-[80vw] -rotate-1 shadow-lg transition-[scale,opacity] duration-150 ${tintClass(store.get(state.activeId)?.tint)} ${overZone ? 'scale-50 opacity-40' : ''}`}
            >
              <div className="card-bar card-name flex items-center py-1.5">
                <span className="truncate">{state.label ?? (displayTitle(store.get(state.activeId)?.title ?? '', (x) => store.get(x)?.title) || 'Untitled')}</span>
              </div>
            </div>
          ) : state.activeId ? (
            <div className={`row drag-overlay transition-[scale,opacity] duration-150 ${overZone ? 'scale-50 opacity-40' : ''}`} style={{ paddingLeft: 0 }}>
              <div className="flex-1">
                <Row id={state.activeId} depth={0} scope="overlay" nav={NOOP_NAV} />
              </div>
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>
    </TreeDndCtx.Provider>
  )
}

/**
 * Drop zones: `zone:top` (Home → its own card), `zone:root:<id>` (a pinned board →
 * a card on it), `zone:tabs` (pin as a tab), `zone:pages` (a top-level page) and
 * `zone:page:<id>` (before/inside/after that page).
 */
function dropOnZone(zone: string, id: string, pageDrop: PageDrop | null, kind: DragState['kind']) {
  if (kind === 'chart') {
    // "chart:<where it lives>:<its id>" onto a tab (or Done): it moves there.
    const [, from, chartId] = id.split(':')
    const to = zone.slice('zone:tab:'.length)
    if (from && chartId && zone.startsWith('zone:tab:')) actions.moveChart(homeOf(from), chartId, homeOf(to))
    return
  }
  const it = store.get(id)
  if (!it) return
  if (zone.startsWith('zone:charts:')) {
    // A card dropped on a tab's charts: it joins the tab (and every chart in it).
    actions.addToTab(zone.slice('zone:charts:'.length), id)
    return
  }
  if (zone === 'zone:notes') {
    actions.toNote(id)
    return
  }
  if (zone.startsWith('zone:card:')) {
    const target = zone.slice('zone:card:'.length)
    if (kind === 'card') {
      if (pageDrop === 'inside') actions.moveInto(id, target)
      else actions.placeCard(id, target, pageDrop === 'after' ? 'after' : 'before')
    }
    return
  }
  if (kind === 'tab') {
    // Reorder: tabs are top-level items, so this moves it among them (cards keep their order).
    const target = zone.startsWith('zone:tab:') ? zone.slice('zone:tab:'.length) : null
    if (target && target !== id) actions.reorder(id, { parentId: null, ...(pageDrop === 'after' ? { afterId: target } : { beforeId: target }) }, { toast: false })
    return
  }
  if (zone === 'zone:newcard' || zone.startsWith('zone:newcard:')) {
    // "+ New card", at the foot of the cards: a card of its own, after the last one.
    const cards = cardBoxes()
    const last = cards[cards.length - 1]?.id
    const parentId = zone === 'zone:newcard' ? null : zone.slice('zone:newcard:'.length)
    if (last && last !== id) actions.placeCard(id, last, 'after', { asNewCard: true })
    else if ((it.parentId ?? null) !== parentId) actions.move(id, { parentId, at: 'end' }, `“${viewItemTitle(id)}” is its own card now`)
    return
  }
  if (zone === 'zone:top' || zone.startsWith('zone:root:')) {
    const parentId = zone === 'zone:top' ? null : zone.slice('zone:root:'.length)
    if (parentId === id) return
    if (it.page) actions.setPage(id, false, { parentId, at: 'end' })
    else if ((it.parentId ?? null) !== parentId) actions.move(id, { parentId, at: 'end' }, parentId ? undefined : 'Moved to its own card')
    return
  }
  if (zone === 'zone:tabs') {
    actions.pinAsTab(id)
    return
  }
  if (zone.startsWith('zone:tab:')) {
    actions.addToTab(zone.slice('zone:tab:'.length), id)
    return
  }
  if (zone === 'zone:pages') {
    actions.setPage(id, true, { parentId: null, at: 'end' })
    return
  }
  if (zone.startsWith('zone:page:')) {
    // "zone:page:<id>" or "zone:page:<id>@<where>" (the same page shown in two places).
    const target = zone.slice('zone:page:'.length).split('@')[0]!
    if (target === id) return
    const parent = store.tree.parentOf.get(target) ?? null
    if (pageDrop === 'before') actions.setPage(id, true, { parentId: parent, beforeId: target })
    else if (pageDrop === 'after') actions.setPage(id, true, { parentId: parent, afterId: target })
    else actions.setPage(id, true, { parentId: target, at: 'end' })
  }
}

/** The board's cards as they sit on screen, in order. */
function cardBoxes(): Array<{ id: string; r: DOMRect }> {
  return [...document.querySelectorAll<HTMLElement>('[data-cards-area] [data-card]')].map((el) => ({ id: el.dataset.card!, r: el.getBoundingClientRect() }))
}
const overACard = (p: { x: number; y: number }) => cardBoxes().some(({ r }) => p.x >= r.left && p.x <= r.right && p.y >= r.top && p.y <= r.bottom)

/** In the open, the card nearest the pointer and which side of it: where a row dropped there becomes a card. */
function newCardSpot(p: { x: number; y: number }): DragState['newCard'] {
  let best: DragState['newCard'] = null
  let far = Infinity
  for (const { id, r } of cardBoxes()) {
    const d = Math.hypot(Math.max(r.left - p.x, 0, p.x - r.right), Math.max(r.top - p.y, 0, p.y - r.bottom))
    if (d < far) {
      far = d
      best = { target: id, side: p.y < r.top + r.height / 2 ? 'before' : 'after' }
    }
  }
  return best
}
const sameSpot = (a: DragState['newCard'], b: DragState['newCard']) => a?.target === b?.target && a?.side === b?.side

/** Lets the provider measure every drop zone again (no ids: all of them), e.g. once a zoom has settled. */
function MeasureBridge({ into }: { into: React.MutableRefObject<(() => void) | null> }) {
  const { measureDroppableContainers } = useDndContext()
  into.current = () => measureDroppableContainers([])
  return null
}

/** Where a drag started on screen (a mouse press or the first touch). */
function pointOf(e: Event | null): { x: number; y: number } | null {
  if (!e) return null
  const t = (e as TouchEvent).touches?.[0] ?? (e as TouchEvent).changedTouches?.[0]
  if (t) return { x: t.clientX, y: t.clientY }
  const m = e as MouseEvent
  return typeof m.clientX === 'number' ? { x: m.clientX, y: m.clientY } : null
}
